import {
  Button,
  Icon,
  IconName,
  LoadingSpinner,
  Pill,
  PILL_COLOR_LIST,
  Popover,
  PopoverContent,
} from '@idexx-laboratories/spot-react';
import dayjs from 'dayjs';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import { GridRendererProps } from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import SelectBoxOptions from '../../components/SelectBoxOptions/SelectBoxOptions';
import TextBoxInCell from '../../components/TextBoxInCell/TextBoxInCell';
import { useStore } from '../../hooks/use-store';
import { isUpgradeScheduled, PracticeConfig, PracticeReport } from '../../types/practice-types';
import { isDefined } from '../../utils/object-utils';
import { getReportsOrigin } from '../../utils/url-utils';

export const MessageCancelUpgrade = (): React.JSX.Element => (
  <>
    <p>Are you sure you want to cancel this upgrade?</p>
    <p>There is no rollback for this operation.</p>
  </>
);

export const MessageCancelUpgradeError = (): React.JSX.Element => (
  <>
    <p>MyCornerstone encountered an error while cancelling the upgrade.</p>
    <p>
      An email has been sent to mycornerstonesupport@idexx.com and the practice contacts have been
      notified.
    </p>
    <p>Please review the summary report and Kaseya for more details.</p>
  </>
);

export const MessageCancelUpgradeCommunicationError = (): React.JSX.Element => (
  <>
    <p>The practice&apos;s upgrade has been cancelled, but no communications were sent.</p>
    <p>
      Use the Summary Report to collect contact information from the practice and notify them of the
      cancellation.
    </p>
    <p>Email mycornerstonesupport@idexx.com as well to notify the MyCornerstone team.</p>
  </>
);

export const MessageConfirmDownloadFilesPresent = (props: {
  agentsList: string | undefined;
}): React.JSX.Element => (
  <>
    <p>You are about to mark the following downloads as complete.</p>
    <>{props.agentsList}</>
    <p>
      Ensure the installation file has been manually placed in the C:\kworking\System folder on each
      listed device before proceeding.
    </p>
  </>
);

export const StatusRenderer = observer((props: GridRendererProps<PracticeConfig>) => {
  const { row } = props;
  if (!row.upgradeDisplayState) return null;
  const { type, title, hint } = row.upgradeDisplayState;
  const pillType: (typeof PILL_COLOR_LIST)[number] =
    type === 'info' ? 'positive' : type === 'warning' ? 'warning' : 'negative';
  const icon: IconName =
    type === 'info' ? 'checkmark' : pillType === 'warning' ? 'alert-notification' : 'cancel';
  return <Pill color={pillType} iconLeft={icon} size='small' title={hint} label={title} />;
});

export const ContactsRenderer = observer((props: GridRendererProps<PracticeConfig>) => {
  const { row } = props;
  return (
    <>
      {(row.contacts || []).map((contact, index) => (
        <React.Fragment key={contact.email}>
          {index > 0 ? ', ' : ''}
          <span className='contact-name'>{contact.name}</span>
        </React.Fragment>
      ))}
    </>
  );
});

export const SapIdRenderer = observer((props: GridRendererProps<PracticeConfig>) => {
  const { row, index } = props;
  const { upgradeManagementStore: store } = useStore();
  const { editItemIndex, isNewItem } = store;

  const changeValue = (newValue: string): void => {
    if (/^\d*$/.test(newValue)) {
      runInAction(() => (row.sapId = newValue));
    }
  };

  if (editItemIndex === index && isNewItem)
    return (
      <TextBoxInCell
        data-testid='edit-text-box_sapId'
        value={row.sapId}
        maxLength={6}
        onChange={e => changeValue(e.target.value)}
      />
    );
  return <>{row.sapId}</>;
});

export const CsVersionRenderer = observer((props: GridRendererProps<PracticeConfig>) => {
  const { row, index } = props;
  const { upgradeManagementStore: store } = useStore();
  const { editItemIndex, csVersions, currentCsVersionIndex } = store;
  const values = useMemo(
    () => csVersions.map(v => (v.default ? `${v.version} (Default)` : v.version)),
    [csVersions],
  );

  if (editItemIndex === index)
    return (
      <FieldGroupInCell htmlFor='edit-text-box_version'>
        <SelectBoxOptions
          id='edit-text-box_version'
          aria-label='Edit Version'
          values={values}
          selectedIndex={currentCsVersionIndex}
          onChange={i => runInAction(() => (store.currentCsVersionIndex = i))}
        />
      </FieldGroupInCell>
    );
  return <>{row.toCsVersion.version}</>;
});

export const ActionsRenderer = observer((props: GridRendererProps<PracticeConfig>) => {
  const { row, index } = props;
  const { upgradeManagementStore: store } = useStore();
  const {
    operatingItemIndex,
    editItemIndex,
    loaders,
    itemsFiltered,
    isNewItem,
    csVersions,
    currentCsVersionIndex,
  } = store;
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const canCancel = useMemo(
    () =>
      isUpgradeScheduled(row) &&
      isDefined(row.scheduleUpgradeTimestamp) &&
      dayjs(row.scheduleUpgradeTimestamp).isAfter(dayjs()),
    [row],
  );

  const isSaveEnabled = () => {
    const item = itemsFiltered[index];
    if (isNewItem) return /^\d{4,6}$/.test(item.sapId);
    return (
      currentCsVersionIndex > -1 &&
      item.toCsVersion.version !== csVersions[currentCsVersionIndex].version
    );
  };

  if (editItemIndex === index) {
    return (
      <>
        {loaders.save ? (
          <LoadingSpinner size='small' />
        ) : (
          <Button
            variant='link'
            size='small'
            iconLeft='save'
            disabled={!isSaveEnabled()}
            onClick={() => void store.saveItem()}
            aria-label='Save Changes'
            data-testid={`save-${index}-btn`}
          />
        )}
        <Button
          variant='link'
          size='small'
          iconLeft='cancel'
          disabled={loaders.save}
          onClick={() => store.cancelEditing()}
          aria-label='Cancel Changes'
          data-testid={`cancel-${index}-btn`}
        />
      </>
    );
  }

  if (editItemIndex > -1) return null;

  return (
    <Popover
      side='bottom'
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      data-testid={`actions-${index}-popover`}
      content={
        <PopoverContent size='small'>
          <Button
            variant='link'
            size='small'
            disabled={(row.reports || []).length === 0}
            onClick={() => {
              store.showUpgradeReports(index);
              setIsOpen(false);
            }}
          >
            View Reports…
          </Button>
          <Button
            variant='link'
            size='small'
            disabled={loaders.refresh}
            onClick={() => {
              void store.downloadQualificationPdf(index);
              setIsOpen(false);
            }}
          >
            Generate Qualification Report
          </Button>
          <Button
            variant='link'
            size='small'
            disabled={row.upgradeStatus === 'Scheduled'}
            onClick={() => {
              store.startEdit(index);
              setIsOpen(false);
            }}
            aria-label='Edit Practice'
            data-testid={`edit-${index}-btn`}
          >
            Edit
          </Button>
          <Button
            variant='link'
            size='small'
            disabled={!store.isMarkFailedDownloadsAsCompleteEnabled(row)}
            onClick={() => {
              store.promptFailedDownloadsAsComplete(index);
              setIsOpen(false);
            }}
          >
            <Loader loading={index === operatingItemIndex}>Mark Downloads Complete</Loader>
          </Button>
          <Button
            variant='link'
            size='small'
            disabled={!canCancel}
            onClick={() => {
              store.promptCancelUpgrade(index);
              setIsOpen(false);
            }}
          >
            <Loader loading={index === operatingItemIndex}>Cancel And Notify</Loader>
          </Button>
        </PopoverContent>
      }
    >
      <span>
        <Icon
          role='icon-button'
          aria-label='Practice Ellipsis'
          iconName='more-2'
          title='Click the action menu to access computer specific options.'
          onClick={() => setIsOpen(!isOpen)}
        />
      </span>
    </Popover>
  );
});

export const ReportLinkRenderer = (props: GridRendererProps<PracticeReport>): React.JSX.Element => {
  const { path, type } = props.row;
  return (
    <a
      href={encodeURI(`${getReportsOrigin()}/${path}`)}
      target='_blank'
      rel='noopener noreferrer'
      className='report-link'
    >
      {type}
    </a>
  );
};

export const dateRenderer = (timestamp: number | undefined): string =>
  timestamp ? dayjs(timestamp).format('MM-DD-YY HH:mm') : '';
